import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';

const ImageSlider = ({ images = [], productName = 'Product' }) => {
  // Find the primary image index, default to 0 if not found
  const getPrimaryImageIndex = () => {
    const primaryIndex = images.findIndex(img => img.isPrimary);
    return primaryIndex >= 0 ? primaryIndex : 0;
  };

  const [currentIndex, setCurrentIndex] = useState(getPrimaryImageIndex());

  // Update current index when images change or primary changes
  useEffect(() => {
    setCurrentIndex(getPrimaryImageIndex());
  }, [images]);

  // Handle case where images is empty or undefined
  if (!images || images.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200">
        <Package className="h-12 w-12 text-gray-400" />
      </div>
    );
  }

  // Single image - no arrows needed
  if (images.length === 1) {
    return (
      <div className="relative w-full h-full">
        <img
          src={images[0].fullUrl || images[0].url}
          alt={productName}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <div className="relative w-full h-full group">
      {/* Main Image */}
      <img
        src={images[currentIndex].fullUrl || images[currentIndex].url}
        alt={`${productName} - Image ${currentIndex + 1}`}
        className="w-full h-full object-cover"
      />

      {/* Previous Button */}
      <button
        type="button"
        onClick={goToPrevious}
        className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
        aria-label="Previous image"
      >
        <ChevronLeft className="h-4 w-4 text-gray-700" />
      </button>

      {/* Next Button */}
      <button
        type="button"
        onClick={goToNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
        aria-label="Next image"
      >
        <ChevronRight className="h-4 w-4 text-gray-700" />
      </button>

      {/* Image Counter */}
      <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded-full">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Dot Indicators */}
      {images.length <= 5 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-4'
                  : 'bg-white/60 hover:bg-white/80'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;
